import type { ChildGuideItem } from '@tunein/web-api-client';
import type {
  PlayerManager,
  LoadOptions as PlayerManagerLoadOptions,
  PlayerManagerOptions,
} from '@tunein/web-player-manager';
import { PlayerStatus } from '@tunein/web-player-manager';
import { registerPlayerManagerEvents } from '@tunein/web-player-manager/react';
import { Singleton, type SingletonInstance } from '@tunein/web-utils';
import { isNotNil } from 'es-toolkit';
import isEmpty from 'lodash/isEmpty';
import {
  fbPixelCustomEvents,
  fbTrack,
} from 'src/client/analytics/facebook/fbTrack';
import { apiBeta } from 'src/client/api';
import { popoutPlayer } from 'src/client/popout';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { stores } from 'src/client/stores';
import { getSearchParam } from 'src/client/utils/getSearchParam.ts';
import { openUpsellAndTrackActivity } from 'src/common/actions/dialog';
import { logClientError, logFeatureActivity } from 'src/common/actions/logging';
import { setGuideItemPathname } from 'src/common/actions/player';
import createAndOpenSubscribeLink from 'src/common/components/utils/createAndOpenSubscribeLink';
import feature from 'src/common/constants/analytics/categoryActionLabel/feature';
import { labels } from 'src/common/constants/analytics/categoryActionLabel/subscribe';
import { BRAZE_PLAY_EVENT } from 'src/common/constants/braze';
import { PLAYER_MANAGER_ENABLED } from 'src/common/constants/experiments/config';
import { BYPASS_UPSELL_DIALOG_ENABLED } from 'src/common/constants/experiments/dialog';
import { PLAY_OPEN_IN_APP_ENABLED } from 'src/common/constants/experiments/redirect';
import {
  selectBranchUrl,
  selectIsBrazeInitialized,
  selectIsMobile,
} from 'src/common/selectors/app';
import { selectAutoPlayGuideItem } from 'src/common/selectors/player';
import { selectBreadcrumbId } from 'src/common/selectors/reporting';
import { brazeLogCustomEvent } from 'src/common/utils/braze/brazeHelper';
import { isInDiscordIFrame } from 'src/common/utils/discord';
import { PLATFORM_ENV_SHORTHAND } from 'src/common/utils/environment';
import getGuideItemPathname from 'src/common/utils/guideItem/getGuideItemPathname';
import isPlayable from 'src/common/utils/guideItem/isPlayable';
import { behaviors as GuideItemBehaviors } from 'src/common/utils/guideItemTypes';
import { setVolume as setVolumeInStorage } from 'src/common/utils/storage/playerVolume';
import { buildConsentData } from './utils';

interface LoadOptions extends PlayerManagerLoadOptions {
  // This is temporary until we completely migrate PM and WAC. This field will be derived in WAC by the help of the WAC
  // init option `uiServiceOptions.generateRoutePathForGuideItem`
  guideItemPathname?: string;
}

export class PlayerSingleton extends Singleton<PlayerManager> {
  #didUseSearchParam = false;
  #canAutoplay = true;

  async init(options: PlayerManagerOptions) {
    const { PlayerManager } = await import('@tunein/web-player-manager');
    const { debug, localizations, enableFetchAutoplayGuideItem } = options;
    const state = storeSingleton.state;

    const player = new PlayerManager({
      api: apiBeta.instance,
      debug,
      localizations,
      enableFetchAutoplayGuideItem,
      playerOptions: {
        debug,
      },
      appSessionData: {
        consentData: buildConsentData(),
        isInDiscordIframe: isInDiscordIFrame(),
        breadcrumbId: selectBreadcrumbId(state),
        env: PLATFORM_ENV_SHORTHAND,
      },
    });

    // Register player events prior to init so that we can capture the autoplay metadata, if available
    registerPlayerManagerEvents(player, stores.playerStore);

    await player.init();

    this.instance = player;
  }

  /**
   * TODO: this can be deleted after the experiment. This is a handy shorthand to help with deciding whether the
   *  experiment is enabled or not.
   */
  get isActive() {
    return apiBeta.getConfigSetting(PLAYER_MANAGER_ENABLED);
  }

  /**
   * Toggle to ensure that an autoplay action won't override a user-initiated play request
   */
  get canAutoplay() {
    return this.#canAutoplay;
  }

  onClick = async (guideItem?: ChildGuideItem, options?: LoadOptions) => {
    this.#canAutoplay = false;
    await this.ready;

    const guideItemToPlay = this.#getGuideItemToPlay(guideItem);

    if (guideItemToPlay) {
      const canContinue = await this.#handleShortCircuits(guideItemToPlay);

      if (canContinue) {
        this.#loadAndPlayGuideItem(guideItemToPlay as ChildGuideItem, options);
      }

      return;
    }

    // Prevents programmatic requests (e.g., async autoplay) from interrupting user-initiated playback
    if (guideItem && options?.isUserInitiated === false) {
      return;
    }

    if (this.instance.isPlaying()) {
      this.instance.isLive() && !this.instance.isLiveSeek()
        ? this.instance.stop()
        : this.instance.pause();
      return;
    }

    if (this.instance.isPaused()) {
      this.instance.play();
    }
  };

  // Convenience wrapper to allow passing reference without consuming args
  playNext = () => {
    return this.instance.playNext();
  };

  seekTo = (seekTime: number) => {
    stores.playerStore.getState().setCurrentTime(seekTime);
    this.instance.seekTo(seekTime);
  };

  seekBackward(seekOffset: number) {
    this.instance.seekBackward(seekOffset);
  }

  seekForward(seekOffset: number) {
    this.instance.seekForward(seekOffset);
  }

  async setAppSessionData(
    appSessionData: Parameters<PlayerManager['setAppSessionData']>[0],
  ) {
    await this.ready;

    this.instance.setAppSessionData(appSessionData);
  }

  async setVolume(volume: number) {
    await this.ready;

    setVolumeInStorage(volume);
    this.instance.setVolume(volume / 100);
  }

  /**
   * Overrides the start position of the topic and is only valid for the initial load of the app session
   */
  get #positionFromSearchParam() {
    const position = this.#didUseSearchParam ? 0 : +getSearchParam('st');

    this.#didUseSearchParam = true;

    return position;
  }

  #getCachedPosition(guideItem: ChildGuideItem) {
    const positionFromSearchParam = this.#positionFromSearchParam;

    if (positionFromSearchParam) {
      return positionFromSearchParam;
    }

    const { time, percent } =
      stores.playerStore.getState().playbackProgressHistory[
        guideItem.playableGuideId as string
      ] || {};

    return percent === 100 ? 0 : time;
  }

  #sendPreLoadMetrics(guideItem: ChildGuideItem, options?: LoadOptions) {
    fbTrack(fbPixelCustomEvents.StartPlay, {
      guideId: guideItem.playableGuideId,
      isAutoplayed: isNotNil(options?.isUserInitiated)
        ? !options.isUserInitiated
        : false,
    });

    const isAutoPlayingRecents =
      guideItem === selectAutoPlayGuideItem(storeSingleton.state);

    if (isAutoPlayingRecents) {
      const { actions, labels: featureLabel } = feature;
      storeSingleton.dispatch(
        logFeatureActivity(actions.autoPlay, featureLabel.playMiniPlayer),
      );
    }
  }

  #sendPostLoadMetrics(guideItem: ChildGuideItem) {
    const state = storeSingleton.state;
    const isBrazeInitialized = selectIsBrazeInitialized(state);

    if (isBrazeInitialized) {
      try {
        const { metadata } = stores.playerStore.getState();

        if (!metadata) {
          return;
        }

        // By sending this custom event to Braze triggers to show the In-App Message. Note: Make sure it happens after the preroll has finished playing.
        brazeLogCustomEvent(BRAZE_PLAY_EVENT, {
          guideId: guideItem.isTopic
            ? guideItem.parentGuideId
            : guideItem.playableGuideId,
          boostEnabledStation: !isEmpty(metadata.boost),
          title: metadata?.header?.title || metadata?.title,
          ...metadata?.classification,
          ...(guideItem.isTopic && {
            topicId: guideItem.playableGuideId,
          }),
        });
      } catch (err) {
        storeSingleton.dispatch(
          logClientError({
            message: `Braze Custom Event: ${BRAZE_PLAY_EVENT} Error`,
            context: {
              error: err,
            },
          }),
        );
        // NOTE: Don't break app functionality for Braze
      }
    }
  }

  async #handleShortCircuits(guideItem: ChildGuideItem) {
    // regular upsell triggered by attempt to play a locked item
    // this instance should only be triggered on the large profile play button
    // because the lock tile covers other cases
    // - blocks playback
    const behaviorAction = guideItem.behaviors?.primaryButton?.actionName;

    if (behaviorAction === GuideItemBehaviors.subscribe) {
      await this.#handleSubscribeAction(guideItem);
      return;
    }

    if (!isPlayable(guideItem)) {
      return;
    }

    if (this.#handleBranchRedirect()) {
      return;
    }

    return true;
  }

  #handleBranchRedirect() {
    const state = storeSingleton.state;

    // on attempt to play, open via branch deep link
    const isMobile = selectIsMobile(state);
    const branchUrl = selectBranchUrl(state);
    const playOpenInAppStore = apiBeta.getConfigSetting(
      PLAY_OPEN_IN_APP_ENABLED,
    );

    if (isMobile && playOpenInAppStore && branchUrl) {
      window.location.replace(branchUrl);

      return true;
    }
  }

  #handleSubscribeAction(guideItem: ChildGuideItem) {
    if (apiBeta.getConfigSetting(BYPASS_UPSELL_DIALOG_ENABLED, false)) {
      createAndOpenSubscribeLink(guideItem, location, null, 'tune.action');
      return;
    }

    return storeSingleton.dispatch(
      openUpsellAndTrackActivity(
        labels.profileUpsellDialog,
        guideItem.playableGuideId,
      ),
    );
  }

  #getGuideItemToPlay(guideItem?: ChildGuideItem) {
    // TODO: this can be deleted after WAC is completely migrated
    if (guideItem) {
      // @ts-ignore
      guideItem.playableGuideId =
        guideItem.actions?.play?.guideId || guideItem.guideId;
      // @ts-ignore
      guideItem.itemToken = guideItem.context?.token;
    }

    const { error, currentGuideItem, metadata, popout } =
      stores.playerStore.getState();
    const isNewGuideItem =
      guideItem &&
      guideItem.playableGuideId !== currentGuideItem?.playableGuideId;
    const isCurrentGuideItemIdle =
      currentGuideItem &&
      this.instance.isIdle() &&
      !metadata?.rejectReason &&
      !popout.showButton;
    const shouldLoadAndPlayGuideItem =
      isNewGuideItem || isCurrentGuideItemIdle || Boolean(error);
    const guideItemToPlay = guideItem || currentGuideItem;

    if (shouldLoadAndPlayGuideItem) {
      return guideItemToPlay as ChildGuideItem;
    }
  }

  async #loadAndPlayGuideItem(
    guideItem: ChildGuideItem,
    options?: LoadOptions,
  ) {
    const { setCurrentGuideItem, setPlayerStatus, setPopoutState } =
      stores.playerStore.getState();

    setCurrentGuideItem(guideItem);
    setPopoutState({ showButton: false });

    if (!options?.guideItemPathname) {
      storeSingleton.dispatch(
        setGuideItemPathname(getGuideItemPathname(guideItem, undefined)),
      );
    }

    this.#sendPreLoadMetrics(guideItem, options);
    await this.#updateAppSessionData();
    await this.instance.load(guideItem, {
      ...options,
      position: this.#getCachedPosition(guideItem),
    });

    // TODO: throw an error event for this case in PM. Handle in error listener in PopoutPlayer.js
    if (this.instance.streamData?.externalPlayerUrl) {
      setPlayerStatus(PlayerStatus.IDLE);
      popoutPlayer.promptUser();
      return;
    }

    if (!this.instance.isLoaded()) {
      return;
    }

    this.#sendPostLoadMetrics(guideItem);
    storeSingleton.dispatch({ type: 'PLAY' });
    await this.instance.play();
  }

  async #updateAppSessionData() {
    const state = storeSingleton.state;

    this.instance.setAppSessionData({
      consentData: await buildConsentData(),
      breadcrumbId: selectBreadcrumbId(state),
    });
  }
}

export const player = new PlayerSingleton() as SingletonInstance<
  PlayerManager,
  PlayerSingleton
>;
